import React, { useState, useEffect, useCallback } from 'react';
import API from '../../api';
import { Download, Upload, Database, Loader2, CheckCircle2, AlertTriangle, FileArchive, Info } from 'lucide-react';
import { useConfirm } from '../../components/ConfirmDialog';
import { downloadBlob, extractAxiosErrorMessage } from '../../utils/download';

const fmtBytes = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

export default function BackupRestoreTab() {
  const confirm = useConfirm();
  const [info, setInfo] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreModalita, setRestoreModalita] = useState('drop_and_insert');
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [restoreError, setRestoreError] = useState('');

  const loadInfo = useCallback(async () => {
    try {
      const r = await API.get('/api/admin/backup/info');
      setInfo(r.data);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => { loadInfo(); }, [loadInfo]);

  const scaricaBackup = async () => {
    setBackupLoading(true);
    try {
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      await downloadBlob(
        API.post('/api/admin/backup', null, { responseType: 'blob' }),
        `edilcima-backup-${ts}.zip`,
        'application/zip',
      );
    } catch (e) {
      alert(`Errore backup: ${await extractAxiosErrorMessage(e)}`);
    } finally {
      setBackupLoading(false);
    }
  };

  const eseguiRestore = async () => {
    if (!restoreFile) return;
    const ok = await confirm(restoreModalita === 'drop_and_insert' ? {
      title: 'Conferma SOVRASCRIVI',
      message: 'Tutte le collezioni del backup verranno SOSTITUITE nel database. I dati attuali andranno persi. Continuare?',
      confirmLabel: 'Sovrascrivi database',
      variant: 'danger',
    } : {
      title: 'Conferma MERGE',
      message: 'I documenti del backup verranno aggiunti al database. Eventuali duplicati saranno ignorati.',
      confirmLabel: 'Procedi con merge',
      variant: 'warning',
    });
    if (!ok) return;

    setRestoreLoading(true);
    setRestoreError('');
    setRestoreResult(null);
    try {
      const fd = new FormData();
      fd.append('file', restoreFile);
      fd.append('modalita', restoreModalita);
      const r = await API.post('/api/admin/restore', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setRestoreResult(r.data);
      setRestoreFile(null);
      loadInfo();
    } catch (e) {
      setRestoreError(e?.response?.data?.detail || 'Errore durante il ripristino');
    } finally {
      setRestoreLoading(false);
    }
  };

  return (
    <div data-testid="backup-restore-tab" className="max-w-5xl space-y-6">
      {/* Info corrente */}
      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-black flex items-center gap-2"><Database size={18} className="text-primary" /> Stato Database</h3>
          {info && <span className="text-xs text-slate-400">DB: <code className="bg-slate-100 px-1.5 rounded">{info.timestamp?.slice(0, 19).replace('T', ' ')}</code></span>}
        </div>
        {!info && <p className="text-sm text-slate-500">Caricamento...</p>}
        {info && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div className="bg-slate-50 rounded p-3">
              <p className="text-xs text-slate-500 uppercase tracking-wide font-bold">Collezioni</p>
              <p className="text-2xl font-black mt-1">{info.totale_collezioni}</p>
            </div>
            <div className="bg-slate-50 rounded p-3">
              <p className="text-xs text-slate-500 uppercase tracking-wide font-bold">Documenti</p>
              <p className="text-2xl font-black mt-1">{info.totale_documenti?.toLocaleString('it-IT')}</p>
            </div>
            <div className="col-span-2 bg-slate-50 rounded p-3">
              <p className="text-xs text-slate-500 uppercase tracking-wide font-bold">Escluse dal backup</p>
              <p className="text-xs text-slate-600 mt-1">{info.escluse?.join(', ') || '-'}</p>
            </div>
          </div>
        )}
      </div>

      {/* BACKUP */}
      <div className="bg-emerald-50 border-2 border-emerald-200 rounded-md p-5">
        <div className="flex items-start gap-3 mb-4">
          <Download size={24} className="text-emerald-700 flex-shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="font-black text-emerald-900 text-lg">Scarica Backup Completo</h3>
            <p className="text-sm text-emerald-800 mt-1">
              Crea un file ZIP con TUTTI i dati del database (prezzario, pratiche, preventivi,
              clienti, fornitori, fatture, training AI). Salvalo in un luogo sicuro.
            </p>
          </div>
        </div>
        <button
          onClick={scaricaBackup}
          disabled={backupLoading}
          data-testid="backup-download-btn"
          className="w-full py-3 bg-emerald-600 text-white font-black rounded-md hover:bg-emerald-700 disabled:opacity-40 transition-all active:scale-95 flex items-center justify-center gap-2"
        >
          {backupLoading
            ? <><Loader2 size={18} className="animate-spin" /> Generazione backup...</>
            : <><FileArchive size={18} /> SCARICA BACKUP (ZIP)</>}
        </button>
      </div>

      {/* RESTORE */}
      <div className="bg-amber-50 border-2 border-amber-200 rounded-md p-5">
        <div className="flex items-start gap-3 mb-4">
          <Upload size={24} className="text-amber-700 flex-shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="font-black text-amber-900 text-lg">Ripristina da Backup</h3>
            <p className="text-sm text-amber-800 mt-1">
              Carica un file ZIP prodotto da questa funzione di backup. Scegli la modalita' di ripristino.
            </p>
          </div>
        </div>

        <div className="space-y-3 bg-white rounded p-4 border border-amber-100">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wide">File ZIP di backup</label>
            <input
              type="file"
              accept=".zip"
              onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
              data-testid="restore-file-input"
              className="w-full text-sm border border-slate-200 rounded-md p-2"
            />
            {restoreFile && (
              <p className="text-xs text-slate-500 mt-1">
                Selezionato: <strong>{restoreFile.name}</strong> ({fmtBytes(restoreFile.size)})
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wide">Modalita di ripristino</label>
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer text-sm p-2 hover:bg-slate-50 rounded">
                <input
                  type="radio"
                  name="modalita"
                  value="drop_and_insert"
                  checked={restoreModalita === 'drop_and_insert'}
                  onChange={(e) => setRestoreModalita(e.target.value)}
                  data-testid="restore-mode-drop"
                  className="mt-0.5"
                />
                <span>
                  <strong className="text-red-700">SOVRASCRIVI</strong> - Le collezioni esistenti vengono cancellate e sostituite con i dati del backup. Usa questa per un ripristino completo da disastro.
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer text-sm p-2 hover:bg-slate-50 rounded">
                <input
                  type="radio"
                  name="modalita"
                  value="merge"
                  checked={restoreModalita === 'merge'}
                  onChange={(e) => setRestoreModalita(e.target.value)}
                  data-testid="restore-mode-merge"
                  className="mt-0.5"
                />
                <span>
                  <strong className="text-emerald-700">MERGE</strong> - Aggiunge i documenti del backup, mantenendo i dati attuali. I duplicati con stesso _id vengono ignorati.
                </span>
              </label>
            </div>
          </div>

          <button
            onClick={eseguiRestore}
            disabled={!restoreFile || restoreLoading}
            data-testid="restore-execute-btn"
            className="w-full py-3 bg-amber-600 text-white font-black rounded-md hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95 flex items-center justify-center gap-2"
          >
            {restoreLoading
              ? <><Loader2 size={18} className="animate-spin" /> Ripristino in corso...</>
              : <><Upload size={18} /> ESEGUI RIPRISTINO</>}
          </button>
        </div>

        {restoreError && (
          <div className="mt-3 bg-red-50 border border-red-300 rounded-md p-3 text-sm text-red-800 flex items-start gap-2">
            <AlertTriangle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
            {restoreError}
          </div>
        )}

        {restoreResult && (
          <div className="mt-3 bg-emerald-50 border border-emerald-300 rounded-md p-4" data-testid="restore-result">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 size={18} className="text-emerald-700" />
              <span className="font-black text-emerald-900">Ripristino completato ({restoreResult.modalita})</span>
            </div>
            <p className="text-sm text-emerald-800 mb-2">
              <strong>{restoreResult.totale_documenti_ripristinati}</strong> documenti in
              <strong> {restoreResult.totale_collezioni_ripristinate}</strong> collezioni
            </p>
            {restoreResult.errors?.length > 0 && (
              <div className="text-xs text-red-700 mb-2">
                <strong>Errori:</strong> {restoreResult.errors.length}
                <pre className="bg-white p-2 rounded mt-1 overflow-x-auto">{restoreResult.errors.join('\n')}</pre>
              </div>
            )}
            <details className="text-xs text-emerald-700">
              <summary className="cursor-pointer font-semibold">Dettaglio per collezione</summary>
              <pre className="mt-2 bg-white p-2 rounded overflow-x-auto">{JSON.stringify(restoreResult.restored, null, 2)}</pre>
            </details>
          </div>
        )}
      </div>

      {/* Tip */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-900 flex items-start gap-2">
        <Info size={16} className="text-blue-700 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-bold mb-1">Consiglio</p>
          <p>Esegui un backup almeno una volta a settimana e salvalo su un servizio cloud esterno (Google Drive, Dropbox). Prima di operazioni critiche (deploy, reset workspace, modifiche massive) crea sempre un backup di sicurezza.</p>
        </div>
      </div>
    </div>
  );
}
